// Privacy Policy — generic UK B2B SaaS template, EHE-aware.
// AI-drafted; legal review recommended before relying on it for litigation
// purposes. Suitable as launch-day baseline.

import { Link } from "react-router-dom";
import LegalShell from "../components/LegalShell";

const LAST_UPDATED = "2 June 2026";

export default function Privacy() {
  return (
    <LegalShell title="Privacy Policy" lastUpdated={LAST_UPDATED}>
      <p>
        This Privacy Policy explains how What Does The Horse Drink Ltd
        (trading as Troughmate, "we", "us", "our") collects, uses, and
        protects information you provide when you use the Troughmate app
        and website (the "Service").
      </p>

      <h2>1. Who we are</h2>
      <p>
        Troughmate is operated by What Does The Horse Drink Ltd, a company
        registered in England and Wales. For the purposes of UK GDPR and
        the Data Protection Act 2018, we are the data controller.
      </p>

      <h2>2. What we collect</h2>
      <p>
        When you create an account and use Troughmate, we collect:
      </p>
      <ul>
        <li>Account information: email address, display name, password (encrypted).</li>
        <li>Yard information: yard name, UK postcode, your role.</li>
        <li>Horse information: name, weight, breed, age, colour, baseline feed.</li>
        <li>Daily logs: water intake, exercise, feed, droppings, behaviour, notes.</li>
        <li>Usage data: pages viewed, features used, errors (anonymised).</li>
        <li>Device data: browser type, IP address (used for security and analytics).</li>
      </ul>

      <h2>3. Why we collect it</h2>
      <p>
        We process your data to:
      </p>
      <ul>
        <li>Operate the Service (provide hydration tracking and EHE calculations).</li>
        <li>Authenticate your account and keep it secure.</li>
        <li>Improve the Service through anonymised usage analysis.</li>
        <li>Contact you about your account, the Service, or critical updates.</li>
      </ul>

      <h2>4. Lawful basis</h2>
      <p>
        We rely on the following lawful bases for processing under UK GDPR:
      </p>
      <ul>
        <li><strong>Contract</strong> for providing the Service you signed up for.</li>
        <li><strong>Legitimate interests</strong> for improving the Service and preventing fraud.</li>
        <li><strong>Consent</strong> for any optional marketing communications.</li>
      </ul>

      <h2>5. Who we share data with</h2>
      <p>
        We use the following third-party processors:
      </p>
      <ul>
        <li><strong>Google Firebase</strong> (authentication, database hosting, cloud storage). Data may be stored on Google servers within the EU/UK.</li>
        <li><strong>Open-Meteo / postcodes.io</strong> (weather lookup from your yard postcode; no personal data sent).</li>
      </ul>
      <p>
        We do not sell your data, share it for advertising, or send your details
        to third parties for marketing purposes.
      </p>

      <h2>6. How long we keep it</h2>
      <p>
        We keep your account and yard data for as long as your account is active.
        If you delete your account, we remove your personal data within 30 days
        (anonymised usage data may be retained for analytics).
      </p>

      <h2>7. Your rights</h2>
      <p>
        Under UK GDPR you have the right to:
      </p>
      <ul>
        <li>Access the personal data we hold about you.</li>
        <li>Correct inaccurate or incomplete data.</li>
        <li>Request deletion of your data ("right to be forgotten").</li>
        <li>Restrict or object to processing.</li>
        <li>Receive your data in a portable format.</li>
        <li>Withdraw consent at any time.</li>
        <li>Lodge a complaint with the Information Commissioner's Office (ICO).</li>
      </ul>
      <p>
        To exercise any of these rights, email{" "}
        <a href="mailto:hello@troughmate.com">hello@troughmate.com</a>.
      </p>

      <h2>8. Cookies and similar technologies</h2>
      <p>
        Troughmate uses cookies only where strictly necessary to operate the
        Service (keeping you signed in, remembering preferences). We do not
        use advertising or third-party tracking cookies.
      </p>

      <h2>9. Security</h2>
      <p>
        We use industry-standard security measures including TLS encryption for
        all data in transit, encrypted password storage, and role-based access
        controls on our backend. No system is perfectly secure; if a breach
        affects your data we will notify you and the ICO within 72 hours
        as required by law.
      </p>

      <h2>10. Children</h2>
      <p>
        Troughmate is intended for use by adults running livery yards. We do
        not knowingly collect data from anyone under the age of 16.
      </p>

      <h2>11. Changes</h2>
      <p>
        We may update this Privacy Policy from time to time. We will post
        material changes on this page and update the "last updated" date.
      </p>

      <h2>12. Contact</h2>
      <p>
        For any privacy questions or to exercise your rights:{" "}
        <a href="mailto:hello@troughmate.com">hello@troughmate.com</a>.
      </p>

      <p style={{ marginTop: 40, fontSize: 13, color: "var(--ink-3)" }}>
        See also our <Link to="/terms">Terms &amp; Conditions</Link>.
      </p>
    </LegalShell>
  );
}
